import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import './AdminOrderReports.css';

function AdminOrderReports() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['order-reports-stats', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/orders/reports/stats?${params}`);
      return res.data;
    },
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['order-reports-revenue', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/orders/reports/revenue-by-day?${params}`);
      return res.data;
    },
  });

  const { data: topProducts, isLoading: productsLoading } = useQuery({
    queryKey: ['order-reports-products', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/orders/reports/top-products?${params}`);
      return res.data;
    },
  });

  const { data: paymentMethods, isLoading: methodsLoading } = useQuery({
    queryKey: ['order-reports-methods', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/orders/reports/by-payment-method?${params}`);
      return res.data;
    },
  });

  const { isLoading: statusLoading } = useQuery({
    queryKey: ['order-reports-status', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/orders/reports/by-status?${params}`);
      return res.data;
    },
  });


  const getPaymentMethodLabel = (method) => {
    const labels = {
      card: 'Mokėjimas kortele',
      cash_on_delivery: 'Grynieji pristatymo metu',
      bank_transfer: 'Banko pavedimas',
    };
    return labels[method] || method;
  };

  if (statsLoading || revenueLoading || productsLoading || methodsLoading || statusLoading) {
    return <div className="admin-order-reports-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-order-reports-page container">
      <div className="reports-header">
        <h1>Užsakymų ataskaitos ir statistika</h1>
      </div>

      {/* Datų filtrai */}
      <div className="filters-section">
        <div className="date-filters">
          <div className="filter-group">
            <label>Nuo datos:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>Iki datos:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                const today = new Date();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(today.getDate() - 30);
                setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
                setDateTo(today.toISOString().split('T')[0]);
              }}
              className="btn btn-secondary"
            >
              Paskutiniai 30 dienų
            </button>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                const today = new Date();
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(today.getDate() - 7);
                setDateFrom(sevenDaysAgo.toISOString().split('T')[0]);
                setDateTo(today.toISOString().split('T')[0]);
              }}
              className="btn btn-secondary"
            >
              Paskutinė savaitė
            </button>
          </div>
        </div>
      </div>

      {/* Pagrindinė statistika */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Bendri užsakymai</h3>
          <p className="stat-value">{stats?.total_orders || 0}</p>
          <p className="stat-label">Užsakymų skaičius</p>
        </div>
        <div className="stat-card">
          <h3>Pajamos</h3>
          <p className="stat-value">{parseFloat(stats?.total_revenue || 0).toFixed(2)} €</p>
          <p className="stat-label">Bendra suma</p>
        </div>
        <div className="stat-card">
          <h3>Vidutinė užsakymo vertė</h3>
          <p className="stat-value">{parseFloat(stats?.avg_order_value || 0).toFixed(2)} €</p>
          <p className="stat-label">Vidutinė suma</p>
        </div>
        <div className="stat-card">
          <h3>Unikalūs klientai</h3>
          <p className="stat-value">{stats?.unique_customers || 0}</p>
          <p className="stat-label">Skirtingų klientų</p>
        </div>
      </div>

      {/* Detali statistika */}
      <div className="reports-grid">
        {/* Statusų statistika */}
        <div className="report-card">
          <h2>Užsakymai pagal statusą</h2>
          <div className="status-list">
            <div className="status-item">
              <span className="status-label">Naujas:</span>
              <span className="status-value">{stats?.orders_pending || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Ruošiamas:</span>
              <span className="status-value">{stats?.orders_processing || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Išsiųstas:</span>
              <span className="status-value">{stats?.orders_shipped || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Įvykdytas:</span>
              <span className="status-value">{stats?.orders_delivered || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Atšauktas:</span>
              <span className="status-value">{stats?.orders_cancelled || 0}</span>
            </div>
          </div>
        </div>

        {/* Mokėjimų statistika */}
        <div className="report-card">
          <h2>Mokėjimų statistika</h2>
          <div className="status-list">
            <div className="status-item">
              <span className="status-label">Apmokėta:</span>
              <span className="status-value">{stats?.orders_paid || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Laukiama:</span>
              <span className="status-value">{stats?.payments_pending || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Nepavyko:</span>
              <span className="status-value">{stats?.payments_failed || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Grąžinta:</span>
              <span className="status-value">{stats?.payments_refunded || 0}</span>
            </div>
          </div>
        </div>

        {/* Finansinė statistika */}
        <div className="report-card">
          <h2>Finansinė statistika</h2>
          <div className="financial-list">
            <div className="financial-item">
              <span className="financial-label">Prekių suma:</span>
              <span className="financial-value">{parseFloat(stats?.total_subtotal || 0).toFixed(2)} €</span>
            </div>
            <div className="financial-item">
              <span className="financial-label">PVM:</span>
              <span className="financial-value">{parseFloat(stats?.total_tax || 0).toFixed(2)} €</span>
            </div>
            <div className="financial-item">
              <span className="financial-label">Pristatymas:</span>
              <span className="financial-value">{parseFloat(stats?.total_shipping || 0).toFixed(2)} €</span>
            </div>
            <div className="financial-item total">
              <span className="financial-label">Bendra suma:</span>
              <span className="financial-value">{parseFloat(stats?.total_revenue || 0).toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Populiariausios prekės */}
        <div className="report-card">
          <h2>Populiariausios prekės</h2>
          {topProducts?.products && topProducts.products.length > 0 ? (
            <div className="products-list">
              {topProducts.products.slice(0, 10).map((product, index) => (
                <div key={product.product_id} className="product-item">
                  <div className="product-rank">#{index + 1}</div>
                  <div className="product-info">
                    <div className="product-name">{product.product_name}</div>
                    <div className="product-stats">
                      <span>Parduota: {product.total_quantity} vnt.</span>
                      <span>Pajamos: {parseFloat(product.total_revenue).toFixed(2)} €</span>
                      <span>Užsakymų: {product.order_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">Nėra duomenų</p>
          )}
        </div>

        {/* Mokėjimo metodai */}
        <div className="report-card">
          <h2>Mokėjimo metodai</h2>
          {paymentMethods?.methods && paymentMethods.methods.length > 0 ? (
            <div className="methods-list">
              {paymentMethods.methods.map((method) => (
                <div key={method.payment_method} className="method-item">
                  <div className="method-name">{getPaymentMethodLabel(method.payment_method)}</div>
                  <div className="method-stats">
                    <span>Užsakymų: {method.order_count}</span>
                    <span>Pajamos: {parseFloat(method.revenue || 0).toFixed(2)} €</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">Nėra duomenų</p>
          )}
        </div>

        {/* Pajamos pagal dieną */}
        <div className="report-card full-width">
          <h2>Pajamos pagal dieną</h2>
          {revenueData?.revenue && revenueData.revenue.length > 0 ? (
            <div className="revenue-table-wrapper">
              <table className="revenue-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Užsakymų</th>
                    <th>Prekių suma</th>
                    <th>PVM</th>
                    <th>Bendra suma</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueData.revenue.map((day) => (
                    <tr key={day.date}>
                      <td>{new Date(day.date).toLocaleDateString('lt-LT')}</td>
                      <td>{day.order_count}</td>
                      <td>{parseFloat(day.subtotal || 0).toFixed(2)} €</td>
                      <td>{parseFloat(day.tax || 0).toFixed(2)} €</td>
                      <td><strong>{parseFloat(day.revenue || 0).toFixed(2)} €</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-message">Nėra duomenų</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminOrderReports;
